import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import ErrorState from './ErrorState';

/**
 * <FieldSharingPanel /> — who sees which property field on a shared link.
 *
 * Joe and Harvey's point: a property record mixes harmless data (name, address,
 * property type), things a maintainer or owner genuinely needs (access route,
 * parking, permits) and things that must never leave the building (key location,
 * security contacts). Section-level toggles are too blunt — every field needs its
 * own "who sees this".
 *
 * A field is INTERNAL until it's explicitly given an audience, so nothing is
 * exposed by accident. The policy is company-wide: setting thirty fields on every
 * property individually would never actually get used.
 */
const FieldSharingPanel = () => {
  const [catalogue, setCatalogue] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [sharing, setSharing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch('/api/contractor/field-sharing');
      setCatalogue(d?.catalogue || []);
      setAudiences(d?.audiences || []);
      setSharing(d?.sharing || {});
      setSaved(d?.sharing || {});
      setDirty(false);
      setError(null);
    } catch (e) { setError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Unsaved sharing changes: warn before leaving or refreshing the page.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const toggle = (fieldId, aud) => {
    setSharing((prev) => {
      const cur = new Set(prev[fieldId] || []);
      if (cur.has(aud)) cur.delete(aud); else cur.add(aud);
      const next = { ...prev };
      if (cur.size) next[fieldId] = [...cur]; else delete next[fieldId];
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/contractor/field-sharing', { method: 'PUT', body: JSON.stringify({ sharing }) });
      toast.success('Sharing settings saved');
      setSaved(sharing);
      setDirty(false);
    } catch (err) { toast.error(`Couldn't save sharing settings: ${apiErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  const sharedCount = Object.keys(sharing).length;

  if (loading && !catalogue.length) return <div className="cw-card cw-card-pad cw-meta" role="status">Loading sharing settings…</div>;
  if (error) return <div className="cw-card cw-card-pad"><ErrorState compact thing="sharing settings" error={error} onRetry={load} retrying={loading} /></div>;

  return (
    <section className="cw-card cw-card-pad" aria-labelledby="cw-field-sharing">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 id="cw-field-sharing" className="cw-h3" style={{ margin: 0 }}>
            <i className="fa-solid fa-eye" style={{ color: 'var(--cw-blue)', marginRight: 6 }} aria-hidden="true" />
            What a shared link can show
            {sharedCount > 0 && <span className="cw-count-pill">{sharedCount} shared</span>}
          </h3>
          <p className="cw-meta" style={{ margin: '2px 0 0' }}>
            Tick who may see each field. Anything left unticked stays internal and never appears on a link.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {dirty && <span className="cw-meta" role="status">Unsaved changes</span>}
          {dirty && <button type="button" className="cw-btn cw-btn-secondary" onClick={() => { setSharing(saved); setDirty(false); }} disabled={saving}>Discard</button>}
          <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={!dirty || saving} aria-busy={saving || undefined}>
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="cw-share-tablewrap">
        <table className="cw-share-table">
          <caption className="cw-sr-only">Which property fields each audience can see on a shared link</caption>
          <thead>
            <tr>
              <th scope="col">Field</th>
              {audiences.map((a) => <th scope="col" key={a.key} title={a.hint}>{a.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {catalogue.map((sec) => (
              <React.Fragment key={sec.section}>
                <tr className="grp"><th scope="colgroup" colSpan={audiences.length + 1} style={{ textAlign: 'left' }}>{sec.label}</th></tr>
                {sec.fields.map((f) => {
                  const id = `${sec.section}.${f.key}`;
                  const on = sharing[id] || [];
                  return (
                    <tr key={id}>
                      <td>
                        {f.label}
                        {f.sensitive && <span className="sens">Sensitive — think twice</span>}
                        {f.always && <span className="always">Always shown</span>}
                      </td>
                      {audiences.map((a) => (
                        <td key={a.key} className="tick">
                          <input
                            type="checkbox"
                            aria-label={`Share ${f.label} with ${a.label}`}
                            disabled={!!f.always}
                            checked={f.always ? true : on.includes(a.key)}
                            onChange={() => toggle(id, a.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="cw-meta" style={{ margin: '10px 0 0' }}>
        Applies to every property. Each portal link is issued to one audience, and shows only that audience's fields.
      </p>
    </section>
  );
};

export default FieldSharingPanel;
